import '@/components/ProfileHeader.css';
import { FileText, MessageCircle, ShieldCheck, MapPin } from 'lucide-react';
import ArtistSpecialisations from '@/components/ArtistSpecialisations';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import usePrerenderReady from '@/lib/usePrerenderReady';
import axios from 'axios';
import VideoSubmissionForm from '@/components/VideoSubmissionForm';
import ProfileShareButton from '@/components/ProfileShareButton';
import ArtistPosterButton from '@/components/ArtistPosterButton';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { useCollaboratorAuth } from '@/context/CollaboratorAuthContext';
import SEO from '@/components/SEO';
import { buildArtistSlug } from '@/lib/artistSlug';
import { resolveIndianArtStarMarker } from '@/lib/indianArtStars';
import { trackEvent } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_VIDEOS = 2;
const TRUST_BADGE_LABELS = {
  verified_profile: 'Verified Profile',
  verified_artist: 'Verified Artist',
  performance_verified: 'Performance Verified',
};

const getPublicTrustBadge = (artist = {}) => {
  const override = String(artist.trust_badge_override || '').trim();
  if (override === 'none') return '';
  if (TRUST_BADGE_LABELS[override]) return TRUST_BADGE_LABELS[override];
  if (artist.badge_label && (artist.is_verified || artist.is_approved)) return artist.badge_label;
  if (artist.is_verified || artist.is_approved) return 'Verified Profile';
  return '';
};

const extractYouTubeId = (url = '') => {
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([\w-]+)/,
    /youtube\.com\/embed\/([\w-]+)/,
    /youtube\.com\/v\/([\w-]+)/,
    /youtube\.com\/(?:shorts|live)\/([\w-]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const detectVideoPlatform = (url = '') => {
  try {
    const host = new URL(String(url || '').trim()).hostname.toLowerCase();
    if (host.includes('youtube.com') || host.includes('youtu.be')) return 'YouTube';
    if (host.includes('instagram.com')) return 'Instagram';
    if (host.includes('facebook.com') || host.includes('fb.watch')) return 'Facebook';
    if (host.includes('vimeo.com')) return 'Vimeo';
    if (host.includes('dailymotion.com') || host.includes('dai.ly')) return 'Dailymotion';
  } catch (error) {
    return 'Video';
  }
  return 'Video';
};

const normalizeSocialUrl = (value = '', fallbackHost = '') => {
  const clean = String(value || '').trim();
  if (!clean) return '';
  if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;
  if (!fallbackHost) return clean;
  return `https://${fallbackHost}/${clean.replace(/^@/, '')}`;
};

const socialProfileDisplayName = (url = '') => {
  try {
    const parsed = new URL(String(url || '').trim());
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    return pathParts[0] ? `@${pathParts[0].replace(/^@/, '')}` : parsed.hostname.replace(/^www\./, '');
  } catch (error) {
    return String(url || '').trim();
  }
};

const formatFollowerCount = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return '';
  if (count >= 1000000) return `${(count / 1000000).toFixed(count >= 10000000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(count >= 100000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return count.toLocaleString('en-IN');
};

const copyTextToClipboard = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error('Copy failed');
  }
};

const renderFormattedInlineText = (text) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${part}-${index}`} className="font-extrabold text-slate-950">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={`${part}-${index}`}>{part.slice(1, -1)}</em>;
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
};

const isStructuredBioLine = (line = '') => {
  const value = String(line || '').trim();
  if (!value) return false;
  if (/^#{1,6}\s+/.test(value)) return true;
  if (/^(\d+)[.)]\s+/.test(value)) return true;
  if (/^[-*•]\s+/.test(value)) return true;
  return false;
};

const normalizeBioSource = (source = '') => {
  const text = String(source || '').replace(/\r/g, '').trim();
  if (!text) return '';
  if (!text.includes('\n') && (text.match(/\s\/\s/g) || []).length >= 3 && text.length > 180) {
    return text.replace(/\s\/\s/g, '\n');
  }

  const rawLines = text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trimEnd());
  const normalizedLines = [];
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const mergedParagraph = paragraphBuffer
      .join(' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+([,.;:!?])/g, '$1')
      .trim();
    if (mergedParagraph) {
      normalizedLines.push(mergedParagraph);
    }
    paragraphBuffer = [];
  };

  rawLines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      if (normalizedLines[normalizedLines.length - 1] !== '') {
        normalizedLines.push('');
      }
      return;
    }

    if (isStructuredBioLine(line)) {
      flushParagraph();
      normalizedLines.push(line);
      return;
    }

    paragraphBuffer.push(line);
  });

  flushParagraph();
  return normalizedLines.join('\n').trim();
};

const withArtistLocationLine = (bio = '', location = '') => {
  const cleanBio = String(bio || '').replace(/\r/g, '').trim();
  const cleanLocation = String(location || '').trim();
  if (!cleanLocation) return cleanBio;
  const locationLine = `**📍 ${cleanLocation}**`;
  if (!cleanBio) return locationLine;
  const lines = cleanBio.split('\n');
  const locationIndex = lines.findIndex((line) => /^(?:\*\*)?📍/.test(line.trim()));
  if (locationIndex >= 0) {
    lines[locationIndex] = locationLine;
    return lines.join('\n').trim();
  }
  return `${locationLine}\n\n${cleanBio}`;
};

const uniqueArtValues = (value = '') =>
  Array.from(new Set(String(value || '').split(',').map((item) => item.trim()).filter(Boolean)));

const buildArtistCardSummary = (artist = {}, fallbackSpeciality = '') => {
  const mainCategories = uniqueArtValues(artist.category);
  const subCategories = uniqueArtValues(artist.subcategory).filter((item) => !mainCategories.includes(item));
  const primaryProfession = String(artist.display_profession || '').trim()
    || subCategories[0]
    || mainCategories[0]
    || fallbackSpeciality
    || 'Creative Artist';
  const secondaryProfession = subCategories.slice(1, 3).join(' / ')
    || mainCategories.slice(1, 3).join(' / ')
    || '';
  return { primaryProfession, secondaryProfession };
};

const FormattedBio = ({ text, fallback }) => {
  const source = normalizeBioSource(text || fallback || '');
  if (!source) return null;

  const lines = source.split(/\r?\n/);
  return (
    <div className="space-y-3 text-base leading-8 text-gray-700" dir="auto">
      {lines.map((rawLine, index) => {
        const line = rawLine.trim();
        if (!line) return <div key={`space-${index}`} className="h-1" />;

        const heading = line.match(/^#{1,6}\s+(.+)$/);
        if (heading) {
          return (
            <h3 key={`heading-${index}`} className="pt-2 text-xl font-extrabold text-slate-950">
              {renderFormattedInlineText(heading[1])}
            </h3>
          );
        }

        const numbered = line.match(/^(\d+)[.)]\s+(.+)$/);
        if (numbered) {
          return (
            <div key={`number-${index}`} className="flex gap-3">
              <span className="mt-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-50 text-xs font-extrabold text-[#C45A00] ring-1 ring-orange-100">
                {numbered[1]}
              </span>
              <p className="min-w-0 flex-1">{renderFormattedInlineText(numbered[2])}</p>
            </div>
          );
        }

        const bullet = line.match(/^[-*•]\s+(.+)$/);
        if (bullet) {
          return (
            <div key={`bullet-${index}`} className="flex gap-3">
              <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-[#FF6F00]" />
              <p className="min-w-0 flex-1">{renderFormattedInlineText(bullet[1])}</p>
            </div>
          );
        }

        return <p key={`line-${index}`}>{renderFormattedInlineText(line)}</p>;
      })}
    </div>
  );
};

const uniqueSeoItems = (items = [], limit = 34) => {
  const seen = new Set();
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .filter((item) => {
      const key = item.replace(/\s+/g, ' ').toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
};

const trimSeoDescription = (value = '', limit = 220) => {
  const clean = String(value || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/[,\s.-]+$/, '')}.`;
};

const formatJourneyDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const hasAcceptedPlatformPolicies = (artist = {}) =>
  Boolean(artist.privacy_policy_accepted && artist.terms_accepted && artist.trust_safety_accepted);

const buildArtistJourney = (artist = {}, videos = []) => {
  const items = [];
  if (artist.created_at) {
    items.push({
      label: 'Profile started',
      date: formatJourneyDate(artist.created_at),
      text: `${artist.name || 'This artist'} joined KalaManch with ${artist.category || artist.subcategory || 'a creative'} profile.`,
    });
  }
  const trustBadge = getPublicTrustBadge(artist);
  if (trustBadge) {
    items.push({
      label: trustBadge,
      date: formatJourneyDate(artist.updated_at),
      text: 'Profile visibility and artist details have passed KalaManch trust review.',
    });
  }
  if (artist.native_language_verified) {
    items.push({
      label: 'Native language badge',
      date: '',
      text: artist.native_language_badge || `Verified ${artist.native_language || 'native language'} profile`,
    });
  }
  if (hasAcceptedPlatformPolicies(artist)) {
    items.push({
      label: 'Policies accepted',
      date: formatJourneyDate(artist.policy_accepted_at),
      text: 'Privacy Policy, Terms & Conditions, and Trust & Safety were accepted for this account.',
    });
  }
  if (artist.is_featured || artist.is_spotlight) {
    items.push({
      label: artist.is_spotlight ? 'Spotlight artist' : 'Featured artist',
      date: '',
      text: 'Selected for higher discovery across KalaManch artist showcases.',
    });
  }
  if (artist.allow_collaborator_enquiries || artist.allow_artist_requests) {
    items.push({
      label: 'Open for work',
      date: '',
      text: 'Available for enquiries, collaboration requests, events, or creative opportunities.',
    });
  }
  if ((artist.portfolio_image_urls || []).length > 0 || videos.length > 0 || artist.youtube || artist.instagram || artist.spotify) {
    items.push({
      label: 'Portfolio ready',
      date: '',
      text: 'Photos, videos, or social portfolio links are available for collaborators to review.',
    });
  }
  if (items.length === 0) {
    items.push({
      label: 'KalaManch profile',
      date: '',
      text: 'Artist journey updates will appear as the profile gets reviewed, featured, and enriched.',
    });
  }
  return items.slice(0, 6);
};

const ArtistJourneyTimeline = ({ artist, videos }) => {
  const journey = buildArtistJourney(artist, videos);
  return (
    <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#00BFA5]">Artist journey</p>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold gradient-text">KalaManch timeline</h2>
        </div>
        <span className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
          {journey.length} milestones
        </span>
      </div>
      <div className="mt-6 space-y-4">
        {journey.map((item, index) => (
          <div key={`${item.label}-${index}`} className="grid grid-cols-[1rem_1fr] gap-4">
            <div className="flex flex-col items-center">
              <span className="mt-1 h-3 w-3 rounded-full bg-[#FF6F00] shadow-[0_0_0_5px_rgba(255,111,0,0.12)]" />
              {index < journey.length - 1 && <span className="mt-2 h-full min-h-10 w-px bg-orange-100" />}
            </div>
            <div className="rounded-2xl border border-orange-100 bg-[#fffaf4] px-4 py-3">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-bold text-gray-950">{item.label}</h3>
                {item.date && <span className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">{item.date}</span>}
              </div>
              <p className="mt-1 text-sm leading-6 text-gray-600">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ArtistProfileReal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { artist: currentArtist } = useArtistAuth();
  const { collaborator } = useCollaboratorAuth();
  const [artist, setArtist] = useState(null);
  const [artistVideos, setArtistVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquirySubject, setEnquirySubject] = useState('');
  const [enquiryMessage, setEnquiryMessage] = useState('');
  const [enquiryStatus, setEnquiryStatus] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [selectedPortfolioImage, setSelectedPortfolioImage] = useState('');
  const [collaboratorAuditions, setCollaboratorAuditions] = useState([]);
  const [selectedAuditionId, setSelectedAuditionId] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  usePrerenderReady(loading || loadingVideos);

  useEffect(() => {
    let mounted = true;
    const fetchArtist = async () => {
      setLoading(true);
      setError('');
      setNotFound(false);
      try {
        const response = await axios.get(`${API}/artists/${id}`, { withCredentials: true, timeout: 45000 });
        if (mounted) {
          setArtist(response.data);
          const canonicalSlug = response.data.slug || buildArtistSlug(response.data);
          if (canonicalSlug && canonicalSlug !== id) {
            window.history.replaceState(null, '', `/artist/${canonicalSlug}`);
          }
        }
      } catch (fetchError) {
        if (mounted) {
          setArtist(null);
          const missing = fetchError.response?.status === 404;
          setNotFound(missing);
          setError(missing ? 'Artist not found' : 'We could not load this profile. Please try again.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchArtist();
    return () => {
      mounted = false;
    };
  }, [id, navigate, loadAttempt]);

  useEffect(() => {
    let mounted = true;
    const fetchVideos = async () => {
      setLoadingVideos(true);
      if (!artist?.id) {
        setArtistVideos([]);
        setLoadingVideos(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/videos/artist/${artist.id}`, { timeout: 8000 });
        if (mounted) setArtistVideos(response.data.videos || []);
      } catch (fetchError) {
        if (mounted) setArtistVideos([]);
      } finally {
        if (mounted) setLoadingVideos(false);
      }
    };
    fetchVideos();
    return () => {
      mounted = false;
    };
  }, [artist?.id]);

  useEffect(() => {
    if (!artist?.id) return;
    trackEvent('artist_profile_view', {
      artist_id: artist.id,
      category: artist.category || '',
      city: artist.city || '',
    });
  }, [artist?.id, artist?.category, artist?.city]);

  useEffect(() => {
    if (!collaborator?.id) {
      setCollaboratorAuditions([]);
      setSelectedAuditionId('');
      return;
    }
    let mounted = true;
    const fetchAuditions = async () => {
      try {
        const response = await axios.get(`${API}/live-auditions`);
        const mine = (response.data.auditions || []).filter((audition) => audition.posted_by_id === collaborator.id);
        if (mounted) {
          setCollaboratorAuditions(mine);
          setSelectedAuditionId(mine[0]?.id || '');
        }
      } catch (fetchError) {
        if (mounted) setCollaboratorAuditions([]);
      }
    };
    fetchAuditions();
    return () => {
      mounted = false;
    };
  }, [collaborator?.id]);

  const handleVideoAdded = (newVideo) => {
    setArtistVideos((prev) => [newVideo, ...prev]);
    setShowVideoForm(false);
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/artists');
  };

  const handleSendEnquiry = async () => {
    if (!collaborator) {
      trackEvent('artist_enquiry_blocked_login_required', { artist_id: artist.id });
      setEnquiryStatus('Sign in as a collaborator to contact this artist.');
      return;
    }
    if (!collaborator.is_verified) {
      trackEvent('artist_enquiry_blocked_admin_approval_required', { artist_id: artist.id });
      setEnquiryStatus('Your collaborator profile must be completed and approved by admin before contacting artists.');
      return;
    }
    try {
      trackEvent('artist_enquiry_submit', { artist_id: artist.id });
      await axios.post(`${API}/enquiries`, { artist_id: artist.id, subject: enquirySubject, message: enquiryMessage }, { withCredentials: true });
      trackEvent('artist_enquiry_success', { artist_id: artist.id });
      setEnquiryStatus('Enquiry sent. You can continue the conversation in your collaborator inbox.');
      setShowEnquiryForm(false);
      setEnquirySubject('');
      setEnquiryMessage('');
    } catch (err) {
      trackEvent('artist_enquiry_error', { artist_id: artist.id });
      setEnquiryStatus(err.response?.data?.detail || 'Failed to send enquiry.');
    }
  };

  if (loading && !artist) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse rounded-3xl bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <div className="h-28 w-28 rounded-full bg-orange-100" />
              <div className="flex-1 space-y-4">
                <div className="h-8 w-2/3 rounded-full bg-slate-100" />
                <div className="h-5 w-1/3 rounded-full bg-slate-100" />
                <div className="h-5 w-1/4 rounded-full bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 px-4 text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-3">{notFound ? 'Artist not found' : 'Profile temporarily unavailable'}</h1>
        {!notFound && <button type="button" onClick={() => setLoadAttempt(attempt => attempt + 1)} className="btn-primary mb-4">Retry loading profile</button>}
        <p className="text-gray-600 mb-6">{error || 'This profile is not available.'}</p>
        <Link to="/artists" className="btn-primary">Back to Artists</Link>
      </div>
    );
  }

  const isOwnProfile = currentArtist?.id === artist?.id;
  const artistAcceptsCollaborators = artist.allow_collaborator_enquiries !== false;
  const artistAcceptsArtists = artist.allow_artist_requests !== false;
  const artistOpenForWork = artistAcceptsCollaborators || artistAcceptsArtists;
  const publicTrustBadge = getPublicTrustBadge(artist);
  const viewerIsApproved = Boolean(isOwnProfile || currentArtist?.is_verified || collaborator?.is_verified);
  const canArtistViewContact = Boolean(currentArtist?.is_verified) && !isOwnProfile && artistAcceptsArtists;
  const canCollaboratorViewContact = Boolean(collaborator?.is_verified) && artistAcceptsCollaborators;
  const canViewContact = Boolean(isOwnProfile) || (viewerIsApproved && (canCollaboratorViewContact || canArtistViewContact));

  const instagramValue = artist.instagram?.trim() || '';
  const uploadedPortfolioImages = [...new Set(
    (Array.isArray(artist.portfolio_image_urls) ? artist.portfolio_image_urls : [])
      .map((url) => String(url || '').trim()).filter(Boolean)
  )];
  const portfolioImageUrls = [
    ...uploadedPortfolioImages,
    ...(uploadedPortfolioImages.length < 3 && artist.image_url ? [artist.image_url] : []),
  ].map((url) => String(url || '').trim()).filter(Boolean).filter((url, index, values) => values.indexOf(url) === index).slice(0, 21);
  const portfolioVideoUrls = (Array.isArray(artist.portfolio_video_urls) ? artist.portfolio_video_urls : [])
    .map((url) => String(url || '').trim())
    .filter(Boolean)
    .slice(0, MAX_VIDEOS);
  const allPortfolioVideos = [
    ...portfolioVideoUrls.map((url, index) => ({
      id: `portfolio-url-${index}`,
      video_url: url,
      video_title: `${artist.name} portfolio video ${index + 1}`,
    })),
    ...artistVideos,
  ].filter((video, index, values) => {
    const videoId = extractYouTubeId(video.video_url || '');
    if (videoId) {
      return values.findIndex((item) => extractYouTubeId(item.video_url || '') === videoId) === index;
    }
    const cleanUrl = String(video.video_url || '').trim();
    return cleanUrl && values.findIndex((item) => String(item.video_url || '').trim() === cleanUrl) === index;
  }).slice(0, MAX_VIDEOS);
  const instagramUrl = normalizeSocialUrl(instagramValue, 'instagram.com');
  const instagramFollowerLabel = formatFollowerCount(artist.instagram_followers);
  const showInstagramFollowers = Boolean(instagramFollowerLabel);
  const InstagramFollowerBadgeElement = instagramUrl ? 'a' : 'span';
  const facebookUrl = normalizeSocialUrl(artist.facebook, 'facebook.com');
  const spotifyUrl = normalizeSocialUrl(artist.spotify);
  const youtubeProfileUrl = normalizeSocialUrl(artist.youtube);
  const availableSocialProfiles = artist.contact_availability?.social_profiles || [];
  const socialProfiles = [
    { label: 'Instagram', url: instagramUrl, available: Boolean(instagramUrl) || availableSocialProfiles.includes('Instagram'), tone: 'border-pink-200 bg-pink-50 text-pink-800' },
    { label: 'Facebook', url: facebookUrl, available: Boolean(facebookUrl) || availableSocialProfiles.includes('Facebook'), tone: 'border-blue-200 bg-blue-50 text-blue-800' },
    { label: 'YouTube', url: youtubeProfileUrl, available: Boolean(youtubeProfileUrl) || availableSocialProfiles.includes('YouTube'), tone: 'border-red-200 bg-red-50 text-red-800' },
    { label: 'Spotify', url: spotifyUrl, available: Boolean(spotifyUrl) || availableSocialProfiles.includes('Spotify'), tone: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  ].filter((profile) => profile.available);
  const whatsappUrl = canViewContact && artist.mobile
    ? `https://wa.me/${String(artist.mobile).replace(/[^\d]/g, '')}`
    : '';
  const visitingCardLinks = canViewContact ? [
    instagramUrl && { label: 'Instagram', url: instagramUrl },
    facebookUrl && { label: 'Facebook', url: facebookUrl },
    youtubeProfileUrl && { label: 'YouTube', url: youtubeProfileUrl },
    spotifyUrl && { label: 'Spotify', url: spotifyUrl },
    whatsappUrl && { label: 'WhatsApp', url: whatsappUrl },
    { label: 'Booking PMB', url: '#enquiry' },
  ].filter(Boolean) : [{ label: 'Booking PMB', url: '#enquiry' }];
  const voiceIntroUrl = String(artist.voice_intro_url || '').trim();
  const canAddMoreVideos = artistVideos.length < MAX_VIDEOS;
  const artistSpeciality = String(artist.display_profession || '').trim() || uniqueArtValues(artist.category)[0] || uniqueArtValues(artist.subcategory)[0] || 'Creative Artist';
  const artistLocation = [artist.city, artist.state, artist.country || 'India']
    .map((value) => String(value || '').trim())
    .filter((value, index, values) => value && values.findIndex((item) => item.toLowerCase() === value.toLowerCase()) === index)
    .join(', ') || 'India';
  const artistCity = artistLocation;
  const publicArtistBio = withArtistLocationLine(artist.bio, artistLocation);
  const epkSlug = artist.slug || buildArtistSlug(artist);
  const indianArtStar = resolveIndianArtStarMarker(artist);
  const artistDescription = trimSeoDescription(
    artist.bio
      ? `${artist.name} is a ${artistSpeciality} from ${artistCity} on Kalamanch.art. ${artist.bio}`
      : `Discover ${artist.name}, a ${artistSpeciality} from ${artistCity}, on Kalamanch.art. View artist profile, portfolio, videos, creative work, and enquiry options.`,
  );
  const artistKeywords = uniqueSeoItems([
    artist.name,
    `${artist.name} ${artist.category || ''}`,
    `${artist.name} ${artistSpeciality}`,
    `${artist.name} ${artistCity}`,
    artist.category,
    artist.subcategory,
    artistCity,
    `${artist.category || artistSpeciality} in ${artistCity}`,
    artist.subcategory ? `${artist.subcategory} in ${artistCity}` : '',
    `hire ${artistSpeciality} in ${artistCity}`,
    `book ${artistSpeciality} in ${artistCity}`,
    `${artistCity} artist`,
    `verified ${artistSpeciality}`,
    artist.native_language,
    artist.native_language_badge,
    artist.native_language ? `${artist.native_language} artist` : '',
    'KalaManch artist profile',
    'Kalamanch.art',
    'Indian artists',
    'hire artists',
    'creative talent India',
    'artist portfolio',
    'event artists India',
    'collaborators',
  ]).join(', ');
  const artistSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: artist.name,
    jobTitle: artistSpeciality,
    image: artist.image_url || undefined,
    description: artist.bio || artistDescription,
    address: artistCity,
    url: window.location.href,
  };
  const visitingCardUrl = window.location.href;
  const shortVisitingCardUrl = visitingCardUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const shortVisitingCardLabel = shortVisitingCardUrl.length > 36 ? `${shortVisitingCardUrl.slice(0, 33)}...` : shortVisitingCardUrl;
  const visitingCardQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=144x144&margin=10&data=${encodeURIComponent(visitingCardUrl)}`;
  const { primaryProfession: artistCardProfession, secondaryProfession: artistCardSecondary } = buildArtistCardSummary(artist, artistSpeciality);
  const copyVisitingCard = async () => {
    try {
      await copyTextToClipboard(visitingCardUrl);
      setCopyStatus('Link copied');
    } catch (copyError) {
      setCopyStatus('Could not copy link');
    }
    window.setTimeout(() => setCopyStatus(''), 2500);
  };

  const inviteToAudition = async () => {
    if (!selectedAuditionId || !artist?.id) return;
    setInviteStatus('Inviting...');
    try {
      await axios.post(
        `${API}/collaborator-auth/live-auditions/${selectedAuditionId}/invites`,
        { artist_id: artist.id },
        { withCredentials: true },
      );
      setInviteStatus('Artist invited to live audition.');
    } catch (requestError) {
      setInviteStatus(requestError.response?.data?.detail || 'Unable to invite artist.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${artist.name} - ${artistSpeciality} in ${artistCity}`}
        description={artistDescription}
        keywords={artistKeywords}
        image={artist.image_url}
        type="profile"
        schema={artistSchema}
      />
      <div className="h-64 sm:h-72 bg-gradient-to-br from-[#4A148C] via-[#FF6F00] to-[#00BFA5] relative">
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute top-24 sm:top-28 left-0 right-0 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <button type="button" onClick={handleBack} className="inline-flex items-center rounded-full bg-white/20 px-4 py-2 text-sm sm:text-base text-white backdrop-blur-sm hover:bg-white/30 transition-colors shadow-sm">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Artists
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="profile-header bg-white rounded-3xl shadow-xl p-5 sm:p-8 mb-8">
          <div className="profile-header-layout">
            <div className="flex-shrink-0">
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white flex items-center justify-center text-4xl sm:text-5xl font-bold text-[#FF6F00]">
                {artist.image_url ? <img src={artist.image_url} alt={`${artist.name} ${artistSpeciality} profile photo on Kalamanch.art`} className="w-full h-full object-contain p-1" /> : artist.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
            </div>
            <div className="profile-header-contents">
              <div className="profile-header-contents">
                <div className="min-w-0 flex-1">
                  <div className="profile-header-name">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">{artist.name}</h1>
                    {showInstagramFollowers && (
                      <InstagramFollowerBadgeElement
                        {...(instagramUrl ? { href: instagramUrl, target: '_blank', rel: 'noreferrer' } : {})}
                        className="inline-flex items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-3 py-1.5 text-sm font-bold text-pink-800 transition hover:-translate-y-0.5 hover:border-pink-300 hover:bg-pink-100 hover:shadow-sm"
                        aria-label={`${artist.name} has ${Number(artist.instagram_followers).toLocaleString('en-IN')} Instagram followers.${instagramUrl ? ' Open Instagram profile.' : ''}`}
                        title={`${Number(artist.instagram_followers).toLocaleString('en-IN')} Instagram followers`}
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="5" />
                          <circle cx="12" cy="12" r="4" />
                          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                        </svg>
                        <span>{instagramFollowerLabel}</span>
                        <span className="font-semibold text-pink-700">followers</span>
                      </InstagramFollowerBadgeElement>
                    )}
                  </div>
                  <p className="text-lg sm:text-xl text-[#FF6F00] font-semibold mb-2">{artistSpeciality}</p>
                  <ArtistSpecialisations key={artist.id} value={artist.subcategory} profession={artistSpeciality} />
                  <div className="profile-header-status">
                    <span>Registered on Kala Manch</span>
                    {publicTrustBadge && <span className="text-green-600 font-medium"><ShieldCheck aria-hidden="true" />{publicTrustBadge}</span>}
                    {artist.native_language_verified && (
                      <span className="text-emerald-700 font-medium">
                        {artist.native_language_badge || `Verified ${artist.native_language || 'native language'} artist`}
                      </span>
                    )}
                    {hasAcceptedPlatformPolicies(artist) && (
                      <span className="text-sky-700 font-medium">Policies accepted</span>
                    )}
                    <span className={`font-semibold ${artistOpenForWork ? 'text-emerald-700' : 'text-gray-500'}`}>
                      {artistOpenForWork ? 'Open for Work' : 'Not Open for Work'}
                    </span>
                  </div>
                </div>
                <div className="profile-header-actions">
                  <ProfileShareButton
                    title={`${artist.name} on Kalamanch.art`}
                    text={`View ${artist.name}'s artist profile on Kalamanch.art`}
                    className="w-full sm:w-auto"
                  />
                  {indianArtStar && (
                    <Link to={indianArtStar.url || `/indian-art/stars/${indianArtStar.slug}`} className="btn-outline w-full sm:w-auto">
                      Star of Indian Art
                    </Link>
                  )}
                  <ArtistPosterButton artist={artist} className="w-full sm:w-auto" />
                  <Link to={`/epk/${epkSlug}`} className="btn-outline w-full sm:w-auto"><FileText aria-hidden="true" />Living EPK</Link>
                  {isOwnProfile ? (
                    <Link to="/my-account" className="btn-primary w-full sm:w-auto">Manage My Account</Link>
                  ) : (
                    <>
                      {collaborator?.is_verified ? (
                        <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => { trackEvent('artist_enquiry_open', { artist_id: artist.id }); setShowEnquiryForm((prev) => !prev); }}><MessageCircle aria-hidden="true" />Send Enquiry</button>
                      ) : collaborator ? (
                        <span className="rounded-full bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 border border-amber-200">
                          Admin approval required to enquire
                        </span>
                      ) : currentArtist ? (
                        canArtistViewContact ? (
                          <span className="rounded-full bg-orange-50 px-4 py-3 text-sm font-semibold text-[#C45A00] border border-orange-100">Artist contact allowed</span>
                        ) : (
                          <span className="rounded-full bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-500 border border-gray-200">Artist requests are turned off</span>
                        )
                      ) : (
                        <button
                          type="button"
                          className="btn-primary w-full sm:w-auto"
                          onClick={() => { trackEvent('artist_enquiry_login_prompt_click', { artist_id: artist.id }); window.dispatchEvent(new Event('openCollaboratorJoin')); }}
                        >
                          <MessageCircle aria-hidden="true" />Sign In to Enquire
                        </button>
                      )}
                      {collaborator?.is_verified && <Link to="/collaborator/inbox" className="btn-outline w-full sm:w-auto">Open Inbox</Link>}
                      {collaborator && collaboratorAuditions.length > 0 && (
                        <div className="w-full rounded-2xl border border-purple-100 bg-purple-50 p-3 sm:min-w-[260px] md:w-auto">
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#4A148C]">Live audition invite</p>
                          <select
                            value={selectedAuditionId}
                            onChange={(event) => setSelectedAuditionId(event.target.value)}
                            className="mt-2 w-full rounded-xl border border-purple-100 bg-white px-3 py-2 text-sm font-semibold text-gray-900"
                          >
                            {collaboratorAuditions.map((audition) => (
                              <option key={audition.id} value={audition.id}>{audition.title}</option>
                            ))}
                          </select>
                          <button type="button" onClick={inviteToAudition} className="mt-2 w-full rounded-full bg-[#4A148C] px-4 py-2 text-sm font-bold text-white">
                            Invite to Audition
                          </button>
                          {inviteStatus && <p className="mt-2 text-xs font-semibold text-gray-600">{inviteStatus}</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-12">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr] xl:items-center">
                <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-900 p-1 shadow-2xl">
                  <div className="relative min-h-[18rem] rounded-[1.55rem] bg-white p-5 sm:p-6">
                    <div className="absolute right-0 top-0 h-28 w-28 rounded-bl-[4rem] bg-orange-100" />
                    <div className="relative flex items-start justify-between gap-4">
                      <img src="/images/kalamanch-logo.png" alt="Kalamanch" className="h-12 w-28 rounded-2xl object-contain" />
                      <span className={`rounded-full border px-3 py-1 text-xs font-extrabold ${artistOpenForWork ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                        {artistOpenForWork ? 'Open for Work' : 'Not Open'}
                      </span>
                    </div>

                      <div className="relative mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
                        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-full border-4 border-white bg-orange-50 shadow-lg">
                          {artist.image_url ? (
                            <img src={artist.image_url} alt={artist.name} className="h-full w-full object-cover" />
                          ) : (
                          <div className="flex h-full w-full items-center justify-center text-4xl font-black text-[#FF6F00]">
                            {artist.name?.charAt(0)?.toUpperCase() || 'A'}
                          </div>
                        )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">KalaManch Artist Card</p>
                          <h2
                            className="mt-2 break-words text-[clamp(1.35rem,4vw,1.8rem)] font-black leading-[1.16] text-slate-950"
                            title={artist.name}
                          >
                            {artist.name}
                          </h2>
                          <p
                            className="mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(0.95rem,3.6vw,1.25rem)] font-bold leading-7 text-[#FF6F00]"
                            title={artistCardProfession}
                          >
                            {artistCardProfession}
                          </p>
                          {artistCardSecondary && (
                            <p className="mt-1 line-clamp-2 text-sm font-semibold leading-6 text-slate-600" title={artistCardSecondary}>{artistCardSecondary}</p>
                          )}
                          <p className="mt-1 text-sm font-semibold text-slate-600">{artistCity}</p>
                        </div>
                      </div>

                    <div className="relative mt-6 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl bg-slate-50 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Portfolio</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">Photos, reels, bio, social links</p>
                      </div>
                      <div className="rounded-2xl bg-emerald-50 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">Contact</p>
                        <p className="mt-1 text-sm font-bold text-slate-900">PMB enquiry on KalaManch</p>
                      </div>
                    </div>

                    <div className="relative mt-5 grid gap-3 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 sm:grid-cols-[minmax(0,1fr)_88px] sm:items-center">
                      <div className="min-w-0">
                        <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Digital card link</span>
                        <span className="mt-1 block max-w-full truncate text-sm font-extrabold text-emerald-800" title={shortVisitingCardUrl}>{shortVisitingCardLabel}</span>
                      </div>
                      <img
                        src={visitingCardQrUrl}
                        alt={`${artist.name} Kalamanch digital visiting card QR code`}
                        className="h-20 w-20 rounded-xl border border-slate-100 bg-white p-1 shadow-sm"
                        loading="lazy"
                      />
                    </div>
                    {visitingCardLinks.length > 0 && (
                      <div className="relative mt-4 flex flex-wrap gap-2">
                        {visitingCardLinks.slice(0, 5).map((link) => (
                          <a
                            key={link.label}
                            href={link.url}
                            target={link.url.startsWith('#') ? undefined : '_blank'}
                            rel={link.url.startsWith('#') ? undefined : 'noopener noreferrer'}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-extrabold text-slate-700"
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-emerald-700">Digital Visiting Card</p>
                  <h2 className="mt-2 text-2xl font-bold text-gray-900">Share {artist.name}'s Kalamanch card</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
                    This works like a digital business card: artist identity, art form, city, portfolio, Instagram, YouTube, Spotify, WhatsApp access where allowed, and PMB enquiry in one shareable profile.
                  </p>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <ProfileShareButton
                      title={`${artist.name} Digital Visiting Card`}
                      text={`View ${artist.name}'s Kalamanch digital visiting card`}
                      url={visitingCardUrl}
                      className="w-full sm:w-auto"
                    />
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        className="btn-outline w-full sm:w-auto"
                        onClick={copyVisitingCard}
                      >
                        Copy Link
                      </button>
                      <span className={`min-h-[1rem] text-xs font-bold ${copyStatus === 'Could not copy link' ? 'text-red-600' : 'text-emerald-700'}`}>
                        {copyStatus}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!isOwnProfile && collaborator?.is_verified && showEnquiryForm && (
              <div id="enquiry" className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
                <h2 className="text-3xl font-bold gradient-text mb-4">Send Enquiry</h2>
                <div className="space-y-4">
                  <input value={enquirySubject} onChange={(event) => setEnquirySubject(event.target.value)} placeholder="Subject" className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                  <textarea value={enquiryMessage} onChange={(event) => setEnquiryMessage(event.target.value)} rows="5" placeholder="Tell the artist what event, project, or requirement you have." className="w-full px-4 py-3 border border-gray-300 rounded-xl" />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button type="button" className="btn-primary" onClick={handleSendEnquiry}>Send Enquiry</button>
                    <button type="button" className="btn-outline" onClick={() => setShowEnquiryForm(false)}>Cancel</button>
                  </div>
                  {enquiryStatus && <p className="text-sm text-gray-600">{enquiryStatus}</p>}
                </div>
              </div>
            )}

            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold gradient-text mb-6">About</h2>
              <FormattedBio text={publicArtistBio} fallback={`${artist.name} is a ${artist.category?.toLowerCase() || 'creative artist'} on Kala Manch.`} />
              {voiceIntroUrl && (
                <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50/70 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Voice Intro</h3>
                      <p className="text-sm text-gray-600">Hear pronunciation, tone, and performance personality.</p>
                    </div>
                    <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-bold text-[#FF6F00]">10-sec intro</span>
                  </div>
                  <audio controls preload="none" src={voiceIntroUrl} className="mt-4 w-full">
                    <a href={voiceIntroUrl} target="_blank" rel="noopener noreferrer">Open voice intro</a>
                  </audio>
                </div>
              )}
      {artist.native_bio && (
        <div className="mt-6 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-bold text-gray-900">Native Language Bio</h3>
            {artist.native_language_verified && (
                      <span className="w-fit rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                        {artist.native_language_badge || `Verified ${artist.native_language || 'native language'} profile`}
              </span>
            )}
          </div>
          <div className="mt-2" dir="auto">
            <FormattedBio text={artist.native_bio} />
          </div>
        </div>
      )}
            </div>

            <ArtistJourneyTimeline artist={artist} videos={allPortfolioVideos} />

            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold gradient-text">Portfolio</h2>
                  {isOwnProfile && <p className="text-sm text-gray-500 mt-1">Maximum {MAX_VIDEOS} YouTube videos per artist profile.</p>}
                </div>
                {isOwnProfile && (
                  <button onClick={() => setShowVideoForm((prev) => !prev)} className="btn-outline text-sm px-4 py-2 w-full sm:w-auto" disabled={!canAddMoreVideos && !showVideoForm}>
                    {showVideoForm ? 'Cancel' : canAddMoreVideos ? '+ Add Video' : 'Limit Reached'}
                  </button>
                )}
              </div>

              {isOwnProfile && showVideoForm && (
                <div className="mb-8">
                  <VideoSubmissionForm artistId={artist.id} artistName={artist.name} currentVideoCount={artistVideos.length} onVideoAdded={handleVideoAdded} />
                </div>
              )}

              {socialProfiles.length > 0 && (
                <div className="mb-8 rounded-3xl border border-gray-100 bg-gray-50/70 p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-bold text-gray-900">Social profiles</h3>
                    {!canViewContact && (
                      <p className="text-xs font-semibold text-gray-500">
                        Links unlock after approved login.
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {socialProfiles.map((profile) => (
                      canViewContact ? (
                        <a
                          key={profile.label}
                          href={profile.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex max-w-full items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition hover:-translate-y-0.5 hover:shadow-sm ${profile.tone}`}
                          title={profile.url}
                        >
                          <span>Open {profile.label}</span>
                          <span className="max-w-[170px] truncate text-xs font-semibold opacity-75">
                            {socialProfileDisplayName(profile.url)}
                          </span>
                        </a>
                      ) : (
                        <span
                          key={profile.label}
                          className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-bold ${profile.tone}`}
                        >
                          {profile.label} available
                        </span>
                      )
                    ))}
                  </div>
                </div>
              )}

              {portfolioImageUrls.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Photos</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {portfolioImageUrls.map((imageUrl, index) => (
                      <button
                        key={`${imageUrl}-${index}`}
                        type="button"
                        onClick={() => setSelectedPortfolioImage(imageUrl)}
                        className="group overflow-hidden rounded-2xl border border-gray-100 bg-gray-50 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-orange-100"
                        aria-label={`Enlarge ${artist.name} portfolio photo ${index + 1}`}
                      >
                        <img
                          src={imageUrl}
                          alt={`${artist.name} portfolio ${index + 1}`}
                          className="aspect-square w-full object-contain bg-white transition duration-300 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                        <span className="block bg-white px-3 py-2 text-xs font-bold text-[#FF6F00]">Click to enlarge</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {loadingVideos ? (
                <div className="text-center py-8 text-gray-500">Loading videos...</div>
              ) : allPortfolioVideos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {allPortfolioVideos.map((video, index) => {
                    const videoId = extractYouTubeId(video.video_url);
                    const platform = video.video_platform || detectVideoPlatform(video.video_url);
                    return (
                      <div key={video.id || index} className="rounded-2xl overflow-hidden shadow-md border border-gray-100">
                        <div className="aspect-video bg-gray-100">
                          {videoId ? (
                            <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${videoId}`} title={video.video_title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                          ) : (
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-gray-950 via-[#1f1235] to-[#FF6F00] px-5 text-center text-white"
                            >
                              <span className="rounded-full bg-white/15 px-4 py-2 text-xs font-black uppercase tracking-[0.18em]">{platform}</span>
                              <span className="text-lg font-black">Open performance video</span>
                              <span className="max-w-xs text-xs text-white/75">This platform restricts website embeds, so the public video opens in a new tab.</span>
                            </a>
                          )}
                        </div>
                        <div className="p-4 bg-white">
                          <h3 className="font-semibold text-gray-800 text-base mb-1">{video.video_title}</h3>
                          {!videoId && <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#FF6F00]">{platform}</p>}
                          {video.description && <p className="text-sm text-gray-600">{video.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No performance videos have been added yet.</div>
              )}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-3xl shadow-lg p-5 sm:p-8">
              <h2 className="text-2xl font-bold gradient-text mb-6">Contact Information</h2>
              {!collaborator && enquiryStatus && (
                <div className="mb-4 rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-[#B45309]">
                  {enquiryStatus}
                </div>
              )}
              {canViewContact ? (
                <div className="space-y-4 text-base text-gray-700">
                  {artist.email && <p><span className="font-semibold">Email:</span> {artist.email}</p>}
                  {artist.mobile && <p><span className="font-semibold">Mobile:</span> {artist.mobile}</p>}
                </div>
              ) : (
                <div className="space-y-4 text-base text-gray-700">
                  <p className="text-gray-600">
                    Contact details and social links are visible only to logged-in, admin-approved KalaManch users to protect artists from scraping and spam.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(artist.email || artist.contact_availability?.email) && (
                      <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-bold text-sky-800">
                        Email available
                      </span>
                    )}
                    {(artist.mobile || artist.contact_availability?.mobile) && (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
                        Phone available
                      </span>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-3 md:w-44">
                    {(currentArtist && !currentArtist.is_verified) || (collaborator && !collaborator.is_verified) ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Your profile must be completed and approved by admin before artist contact links are visible.
                      </div>
                    ) : currentArtist ? (
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        This artist has turned off artist-to-artist contact for now.
                      </div>
                    ) : (
                      <button type="button" className="btn-primary w-full" onClick={() => window.dispatchEvent(new Event('openCollaboratorJoin'))}>
                        Collaborator Sign In
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {selectedPortfolioImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedPortfolioImage('')}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-white px-4 py-2 text-sm font-bold text-gray-900 shadow-lg"
            onClick={() => setSelectedPortfolioImage('')}
          >
            Close
          </button>
          <img
            src={selectedPortfolioImage}
            alt={`${artist.name} portfolio enlarged`}
            className="max-h-[86vh] max-w-[92vw] rounded-2xl bg-white object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ArtistProfileReal;
